import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  Globe,
  Loader2,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import {
  Button,
  Card,
  Chip,
  DeviceFrame,
  LiveDot,
  NaraAvatar,
  ProvenanceTag,
  Sheet,
} from "@/ui";
import { brandKit, templates as sampleTemplates } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import {
  useActiveTheme,
  useCreatePage,
  useDomains,
  usePage,
  usePages,
  useTemplates,
  useUpdatePage,
} from "@/data/hooks";
import {
  builder,
  type DomainItem,
  type Page,
  type Template as RealTemplate,
} from "@/data/services";
import { ApiError } from "@/api/client";
import { TreeRenderer } from "@/editor/TreeRenderer";
import { ThemeStyleInjector } from "@/editor/theme/ThemeStyleInjector";
import { fmt } from "@/lib/format";
import { cn } from "@/lib/cn";
import { TemplateThumb } from "./TemplateThumb";

const ALL = "All";

/** Tiny mono "sample" chip — provenance so live vs sample is never ambiguous. */
function SampleChip() {
  return (
    <span className="inline-flex items-center rounded-chip bg-paper-2 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-subtle dark:bg-white/5">
      sample
    </span>
  );
}

/* ------------------------------ Template VM ------------------------------- */

/** One shape that renders both real + sample templates in a single card. */
interface TemplateVM {
  id: string;
  name: string;
  kind: RealTemplate["kind"];
  category: string;
  blurb: string;
  colors: [string, string];
  views?: number;
  sample: boolean;
  raw?: RealTemplate;
}

function fromReal(t: RealTemplate): TemplateVM {
  return {
    id: t.id,
    name: t.name,
    kind: t.kind,
    category: t.category,
    blurb: t.blurb,
    colors: [t.thumbnail_colors[0] ?? "#B84422", t.thumbnail_colors[1] ?? "#C8923D"],
    views: t.published_views,
    sample: false,
    raw: t,
  };
}

function fromSample(t: (typeof sampleTemplates)[number]): TemplateVM {
  return {
    id: t.id,
    name: t.name,
    kind: t.kind,
    category: t.category,
    blurb: t.blurb,
    colors: t.thumb,
    views: t.views,
    sample: true,
  };
}

/* ----------------------------- Your pages (real) -------------------------- */

const STATUS_LABEL: Record<Page["status"], string> = {
  draft: "Draft",
  staging: "Staging",
  published: "Live",
  archived: "Archived",
};

/** Page status filter — label → status (Live maps to the published status). */
const STATUS_FILTERS: { label: string; status: Page["status"] }[] = [
  { label: "Draft", status: "draft" },
  { label: "Staging", status: "staging" },
  { label: "Live", status: "published" },
  { label: "Archived", status: "archived" },
];

/** Sort order so live pages always surface first, archived last. */
const STATUS_RANK: Record<Page["status"], number> = {
  published: 0,
  staging: 1,
  draft: 2,
  archived: 3,
};

/** A page is "publishable live" only when it has a real custom hostname. Dev
 *  has no public render at :8088, so without a dotted custom domain there is
 *  nowhere honest to open. */
function isCustomDomain(domain?: string): domain is string {
  return !!domain && domain.includes(".");
}
function viewLiveUrl(page: Page): string {
  return `https://${page.domain}${page.slug ? `/${page.slug}` : ""}`;
}

/** Title → URL-safe slug. */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/** Map a backend domain status string onto our pill vocabulary. */
function normalizeDomainStatus(s: string): "live" | "pending" | "error" {
  const v = s.toLowerCase();
  if (v === "live" || v === "verified" || v === "active") return "live";
  if (v === "error" || v === "failed" || v === "invalid") return "error";
  return "pending";
}

/** Pull a human message out of a failed mutation. */
function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string } | string | null;
    if (body && typeof body === "object" && body.message) return body.message;
  }
  return fallback;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

/** A stable hue pair for a page thumbnail, derived from its id. */
function pageThumb(id: string): [string, string] {
  const palettes: [string, string][] = [
    ["#3E7C74", "#5B6470"],
    ["#C8923D", "#8A857C"],
    ["#5B6470", "#3E7C74"],
    ["#8A857C", "#C8923D"],
  ];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palettes[h % palettes.length]!;
}

function PageStatusChip({ status }: { status: Page["status"] }) {
  if (status === "published") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-chip bg-paper-2 px-2 py-0.5 dark:bg-white/5">
        <LiveDot />
        <span className="font-mono text-[10px] uppercase tracking-wide text-ink-strong">live</span>
      </span>
    );
  }
  return <Chip mono>{STATUS_LABEL[status]}</Chip>;
}

/** A compact, tooltip-labelled icon action used in the page-card footer. */
function CardAction({
  icon: Icon,
  label,
  onClick,
  disabled,
  accent,
}: {
  icon: typeof Pencil;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick();
      }}
      className={cn(
        "grid h-8 w-8 place-items-center rounded-full transition-colors",
        disabled
          ? "cursor-not-allowed text-subtle/60"
          : accent
            ? "text-accent hover:bg-accent-soft"
            : "text-muted hover:bg-paper-2 hover:text-ink dark:hover:bg-white/5",
      )}
    >
      <Icon className="h-[15px] w-[15px]" strokeWidth={1.5} />
    </button>
  );
}

function PageCard({
  page,
  onPreview,
  onEdit,
  onSettings,
}: {
  page: Page;
  onPreview: () => void;
  onEdit: () => void;
  onSettings: () => void;
}) {
  const thumb = useMemo(() => pageThumb(page.id), [page.id]);
  const liveable = isCustomDomain(page.domain);
  return (
    <Card interactive onClick={onPreview} className="group flex flex-col overflow-hidden">
      <TemplateThumb thumb={thumb} />
      <div className="flex flex-1 flex-col p-3.5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[14px] font-medium leading-snug text-ink-strong">{page.title}</p>
          <ArrowUpRight
            className="mt-0.5 h-4 w-4 shrink-0 text-subtle transition-colors group-hover:text-ink"
            strokeWidth={1.5}
          />
        </div>
        <p className="mt-0.5 line-clamp-1 font-mono text-[12px] text-muted">/{page.slug}</p>
        <p className="mt-1 flex items-center gap-1.5 font-mono text-[11px] text-subtle">
          <Globe className="h-3 w-3 shrink-0" strokeWidth={1.5} />
          <span className="truncate">{liveable ? page.domain : "No custom domain"}</span>
        </p>
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <PageStatusChip status={page.status} />
          <span className="font-mono text-[11px] text-subtle">
            {page.updated_at ? `updated ${relativeTime(page.updated_at)}` : page.locale}
          </span>
        </div>

        {/* per-page actions — never ambiguous, never crash the card */}
        <div className="mt-3 flex items-center gap-0.5 border-t border-hairline/60 pt-2">
          <CardAction icon={Pencil} label="Edit in maker" accent onClick={onEdit} />
          <CardAction icon={Eye} label="Preview page" onClick={onPreview} />
          <CardAction
            icon={ExternalLink}
            label={liveable ? "View live" : "Assign a custom domain to publish live"}
            disabled={!liveable}
            onClick={() => window.open(viewLiveUrl(page), "_blank", "noopener,noreferrer")}
          />
          <span className="flex-1" />
          <CardAction icon={Settings2} label="Domain & settings" onClick={onSettings} />
        </div>
      </div>
    </Card>
  );
}

/** Calm placeholder tiles while pages/templates load — same footprint, no layout shift. */
function PagesSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="overflow-hidden">
          <div className="aspect-[4/3] animate-pulse bg-paper-2 dark:bg-white/5" />
          <div className="space-y-2 p-3.5">
            <div className="h-3.5 w-3/4 animate-pulse rounded bg-paper-2 dark:bg-white/5" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-paper-2 dark:bg-white/5" />
          </div>
        </Card>
      ))}
    </div>
  );
}

export function LibraryScreen() {
  const nav = useNavigate();
  const { currentProjectId, live, current } = useWorkspace();
  const [active, setActive] = useState<string>(ALL);
  const [preview, setPreview] = useState<TemplateVM | null>(null);
  const [pagePreview, setPagePreview] = useState<Page | null>(null);
  const [sheetFocusDomain, setSheetFocusDomain] = useState(false);
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [instantiatingId, setInstantiatingId] = useState<string | null>(null);

  // Real pages — only query when we have a project AND a live backend.
  const canQueryPages = live && !!currentProjectId;
  const pagesQ = usePages(canQueryPages ? currentProjectId : undefined);

  const realPages = pagesQ.data ?? [];
  const pagesLoading = canQueryPages && pagesQ.isLoading;
  const pagesError = canQueryPages && pagesQ.isError;
  // Sample fallback whenever there's no project, the backend isn't live, or the
  // query failed (e.g. 401). Honest: shown with a "sample" chip.
  const pagesAreSample = !canQueryPages || pagesError;

  /* ---- page filters + search (v1 feature restored) ---- */
  const [pageSearch, setPageSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<Page["status"] | "all">("all");
  const [localeFilter, setLocaleFilter] = useState<string>(ALL);

  const locales = useMemo(() => {
    const seen: string[] = [];
    for (const p of realPages) if (p.locale && !seen.includes(p.locale)) seen.push(p.locale);
    return seen;
  }, [realPages]);

  const filteredPages = useMemo(() => {
    const q = pageSearch.trim().toLowerCase();
    return realPages
      .filter((p) => {
        if (statusFilter !== "all" && p.status !== statusFilter) return false;
        if (localeFilter !== ALL && p.locale !== localeFilter) return false;
        if (q && !p.title.toLowerCase().includes(q) && !p.slug.toLowerCase().includes(q))
          return false;
        return true;
      })
      // Live first, then staging / draft / archived; newest within a group.
      .sort((a, b) => {
        const r = STATUS_RANK[a.status] - STATUS_RANK[b.status];
        if (r !== 0) return r;
        return (b.updated_at ?? "").localeCompare(a.updated_at ?? "");
      });
  }, [realPages, pageSearch, statusFilter, localeFilter]);

  /* ---- templates → REAL (BC-2) with sample fallback ---- */
  const canQueryTemplates = live && !!currentProjectId;
  const templatesQ = useTemplates(
    canQueryTemplates ? currentProjectId : undefined,
    active === ALL ? undefined : active,
  );
  const realTemplates = templatesQ.data?.items ?? [];
  const templatesLoading = canQueryTemplates && templatesQ.isLoading;
  const templatesError = canQueryTemplates && templatesQ.isError;
  // Sample fallback: no project / not live / query failed.
  const templatesAreSample = !canQueryTemplates || templatesError;

  const templateCards: TemplateVM[] = useMemo(
    () =>
      templatesAreSample
        ? (active === ALL
            ? sampleTemplates
            : sampleTemplates.filter((t) => t.category === active)
          ).map(fromSample)
        : realTemplates.map(fromReal),
    [templatesAreSample, realTemplates, active],
  );

  // Categories: derive from the real list when live, sample categories otherwise.
  const categories = useMemo(() => {
    const seen: string[] = [];
    const src = templatesAreSample ? sampleTemplates : realTemplates;
    for (const t of src) if (!seen.includes(t.category)) seen.push(t.category);
    return [ALL, ...seen];
  }, [templatesAreSample, realTemplates]);

  const makeFromTemplate = async (t: TemplateVM) => {
    // Sample template → carry intent into /make as a prompt.
    if (t.sample || !t.raw || !currentProjectId) {
      nav("/make", { state: { prompt: `Start from the "${t.name}" template` } });
      return;
    }
    // Real template → instantiate a fresh page, then open it in the maker.
    setInstantiatingId(t.id);
    try {
      const page = await builder.instantiateTemplate(t.raw.id, { project_id: currentProjectId });
      setPreview(null);
      nav("/make", { state: { pageId: page.id, prompt: `Edit "${page.title}"` } });
    } catch {
      // Quiet failure — keep the card usable; don't crash the gallery.
    } finally {
      setInstantiatingId(null);
    }
  };

  const openPage = (p: Page) =>
    nav("/make", { state: { pageId: p.id, prompt: `Edit "${p.title}"` } });

  const openPreview = (p: Page) => {
    setSheetFocusDomain(false);
    setPagePreview(p);
  };
  const openSettings = (p: Page) => {
    setSheetFocusDomain(true);
    setPagePreview(p);
  };

  return (
    <div className="stagger space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">Library</p>
          <h1 className="text-[24px] font-semibold tracking-tight text-ink-strong md:text-[28px]">
            Your shelf of saved and ready-made work
          </h1>
        </div>
        <Button variant="ghost" size="md" onClick={() => nav("/make")}>
          <Sparkles className="h-4 w-4 text-accent" strokeWidth={1.75} />
          New with Nara
        </Button>
      </header>

      {/* ------------------------------ Your pages ----------------------------- */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="eyebrow mb-1 flex items-center gap-2">
              Your pages {pagesAreSample && <SampleChip />}
            </p>
            <h2 className="text-[15px] font-semibold tracking-tight text-ink-strong">
              Everything you've built so far
            </h2>
          </div>
          <Button variant="coral" size="sm" onClick={() => setNewPageOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            New web page
          </Button>
        </div>

        {/* filters + search — only when there are real pages */}
        {!pagesAreSample && realPages.length > 0 && (
          <div className="space-y-2.5">
            <label className="relative block">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
                strokeWidth={1.5}
              />
              <input
                type="text"
                value={pageSearch}
                onChange={(e) => setPageSearch(e.target.value)}
                placeholder="Search pages by title or slug…"
                className="h-11 w-full rounded-btn border border-hairline bg-paper pl-9 pr-3 font-mono text-[13px] text-ink-strong placeholder:text-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent dark:bg-white/[0.03]"
              />
            </label>

            <div className="no-scrollbar -mx-1 flex flex-wrap gap-2 px-1">
              <Chip active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>
                {ALL}
              </Chip>
              {STATUS_FILTERS.map((f) => (
                <Chip
                  key={f.status}
                  active={statusFilter === f.status}
                  onClick={() => setStatusFilter(f.status)}
                >
                  {f.label}
                </Chip>
              ))}
            </div>

            {locales.length > 1 && (
              <div className="no-scrollbar -mx-1 flex flex-wrap gap-2 px-1">
                <Chip mono active={localeFilter === ALL} onClick={() => setLocaleFilter(ALL)}>
                  All locales
                </Chip>
                {locales.map((loc) => (
                  <Chip
                    key={loc}
                    mono
                    active={localeFilter === loc}
                    onClick={() => setLocaleFilter(loc)}
                  >
                    {loc}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        )}

        {pagesLoading ? (
          <PagesSkeleton />
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {/* start from scratch — dashed motif, always first */}
            <button
              onClick={() => setNewPageOpen(true)}
              className="group flex min-h-[44px] flex-col items-center justify-center gap-3 rounded-card border border-dashed border-hairline bg-paper-2/40 p-5 text-center transition-colors hover:border-accent/40 hover:bg-paper-2 dark:bg-white/[0.02]"
            >
              <span className="grid h-11 w-11 place-items-center rounded-full border border-dashed border-hairline text-muted transition-colors group-hover:border-accent/50 group-hover:text-ink">
                <Plus className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <span className="text-[13px] font-medium text-ink-strong">Add a web page</span>
              <span className="text-[12px] leading-snug text-muted">
                name it, pick a domain, open the editor
              </span>
            </button>

            {pagesAreSample ? (
              // Sample fallback: reuse the template fixtures as stand-in "pages".
              sampleTemplates.slice(0, 3).map((t) => (
                <Card
                  key={`sample-${t.id}`}
                  interactive
                  onClick={() => setPreview(fromSample(t))}
                  className="group overflow-hidden"
                >
                  <TemplateThumb thumb={t.thumb} />
                  <div className="p-3.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[14px] font-medium leading-snug text-ink-strong">{t.name}</p>
                      <ArrowUpRight
                        className="mt-0.5 h-4 w-4 shrink-0 text-subtle transition-colors group-hover:text-ink"
                        strokeWidth={1.5}
                      />
                    </div>
                    <p className="mt-0.5 line-clamp-1 text-[12px] text-muted">{t.blurb}</p>
                    <div className="mt-2.5 flex items-center justify-between gap-2">
                      <Chip mono>Draft</Chip>
                      <SampleChip />
                    </div>
                  </div>
                </Card>
              ))
            ) : realPages.length === 0 ? (
              // Graceful empty — live backend, but no pages yet.
              <div className="col-span-full flex flex-col items-center justify-center gap-3 rounded-card border border-hairline bg-paper-2/40 p-10 text-center dark:bg-white/[0.02] md:col-span-2 lg:col-span-3">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-paper-2 text-muted dark:bg-white/5">
                  <FileText className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <p className="text-[14px] font-medium text-ink-strong">No pages yet</p>
                <p className="max-w-[34ch] text-[12px] leading-snug text-muted">
                  Start from scratch or let Nara draft your first landing page.
                </p>
              </div>
            ) : filteredPages.length === 0 ? (
              // Graceful empty — filters/search exclude everything.
              <div className="col-span-full flex flex-col items-center justify-center gap-3 rounded-card border border-hairline bg-paper-2/40 p-10 text-center dark:bg-white/[0.02] md:col-span-2 lg:col-span-3">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-paper-2 text-muted dark:bg-white/5">
                  <Search className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <p className="text-[14px] font-medium text-ink-strong">No pages match</p>
                <p className="max-w-[34ch] text-[12px] leading-snug text-muted">
                  Try a different search or clear the filters.
                </p>
              </div>
            ) : (
              filteredPages.map((p) => (
                <PageCard
                  key={p.id}
                  page={p}
                  onPreview={() => openPreview(p)}
                  onEdit={() => openPage(p)}
                  onSettings={() => openSettings(p)}
                />
              ))
            )}
          </div>
        )}
      </section>

      {/* ------------------------------ Templates ------------------------------ */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="eyebrow mb-1">Templates</p>
            <h2 className="text-[15px] font-semibold tracking-tight text-ink-strong">
              Ready-made starting points
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {templatesAreSample ? (
              <SampleChip />
            ) : (
              <span className="font-mono text-[10px] lowercase tracking-wide text-subtle">
                via builder-api
              </span>
            )}
          </div>
        </div>

        {/* category filter */}
        <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1">
          {categories.map((c) => (
            <Chip key={c} active={active === c} onClick={() => setActive(c)}>
              {c}
            </Chip>
          ))}
        </div>

        {/* gallery */}
        {templatesLoading ? (
          <PagesSkeleton />
        ) : templateCards.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-card border border-hairline bg-paper-2/40 p-10 text-center dark:bg-white/[0.02]">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-paper-2 text-muted dark:bg-white/5">
              <Sparkles className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <p className="text-[14px] font-medium text-ink-strong">No templates yet</p>
            <p className="max-w-[34ch] text-[12px] leading-snug text-muted">
              Save a page as a template to start your own gallery.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {templateCards.map((t) => (
              <TemplateCard
                key={t.id}
                t={t}
                pending={instantiatingId === t.id}
                onOpen={() => setPreview(t)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ------------------------------ Brand kit ------------------------------ */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="eyebrow mb-1">Brand kit</p>
            <h2 className="text-[15px] font-semibold tracking-tight text-ink-strong">
              The look every page inherits
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <SampleChip />
            <span className="font-mono text-[11px] text-subtle">{brandKit.logoMark}</span>
          </div>
        </div>

        <Card className="p-5">
          <div className="grid gap-6 md:grid-cols-3">
            {/* colors */}
            <div>
              <p className="eyebrow mb-3">Colors</p>
              <div className="space-y-2.5">
                {brandKit.colors.map((c) => (
                  <div key={c.name} className="flex items-center gap-3">
                    <span
                      className="h-8 w-8 shrink-0 rounded-btn border border-hairline"
                      style={{ background: c.hex }}
                    />
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-ink-strong">{c.name}</p>
                      <p className="font-mono text-[11px] uppercase text-subtle">{c.hex}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* fonts */}
            <div>
              <p className="eyebrow mb-3">Type</p>
              <div className="space-y-2.5">
                {brandKit.fonts.map((f) => (
                  <div key={f.role} className="rounded-btn bg-paper-2 px-3 py-2 dark:bg-white/5">
                    <p className="font-mono text-[10px] uppercase tracking-wide text-subtle">
                      {f.role}
                    </p>
                    <p className="text-[14px] text-ink-strong">{f.family}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* voice */}
            <div>
              <p className="eyebrow mb-3">Voice</p>
              <div className="flex items-start gap-3 rounded-btn bg-paper-2 px-3 py-3 dark:bg-white/5">
                <NaraAvatar size="sm" />
                <p className="text-[14px] leading-relaxed text-ink">{brandKit.voice}</p>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* preview sheet — templates (real or sample) */}
      <Sheet open={preview !== null} onClose={() => setPreview(null)} title={preview?.name}>
        {preview && (
          <div className="space-y-4">
            <TemplateThumb thumb={preview.colors} ratio="aspect-[16/10]" className="rounded-card" />

            <div className="flex flex-wrap items-center gap-2">
              <KindChip kind={preview.kind} />
              <Chip>{preview.category}</Chip>
              {preview.sample && <SampleChip />}
              {preview.kind === "published" && preview.views != null && (
                <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-subtle">
                  <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {fmt(preview.views)} views
                </span>
              )}
            </div>

            <p className="text-[14px] leading-relaxed text-muted">{preview.blurb}</p>

            <div className="grid grid-cols-2 gap-3 rounded-card bg-paper-2 p-4 dark:bg-white/5">
              <Detail label="Type" value={cap(preview.kind)} />
              <Detail label="Category" value={preview.category} />
              <Detail
                label="Status"
                value={preview.kind === "published" ? "Live" : "Draft template"}
              />
              <Detail label="Best for" value="Mobile-first launch" />
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              {preview.sample ? (
                <ProvenanceTag source="posthog" />
              ) : (
                <span className="font-mono text-[10px] lowercase tracking-wide text-subtle">
                  via builder-api
                </span>
              )}
              <Button
                variant="coral"
                size="lg"
                disabled={instantiatingId === preview.id}
                onClick={() => makeFromTemplate(preview)}
              >
                {instantiatingId === preview.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                ) : (
                  <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                )}
                Make it mine
              </Button>
            </div>
          </div>
        )}
      </Sheet>

      {/* preview + settings sheet — real page */}
      {pagePreview && (
        <PageSheet
          page={pagePreview}
          projectId={canQueryPages ? currentProjectId : undefined}
          focusDomain={sheetFocusDomain}
          onClose={() => setPagePreview(null)}
          onEdit={() => openPage(pagePreview)}
        />
      )}

      {/* add a web page */}
      {newPageOpen && (
        <NewPageSheet
          projectId={canQueryPages ? currentProjectId : undefined}
          propertyDomain={live ? current.domain : undefined}
          onClose={() => setNewPageOpen(false)}
          onOpenInEditor={(p) => {
            setNewPageOpen(false);
            openPage(p);
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------- Template card ---------------------------- */

/** Mono kind chip — quiet provenance for what a tile is. */
function KindChip({ kind }: { kind: RealTemplate["kind"] }) {
  if (kind === "published") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-chip bg-paper-2 px-2 py-0.5 dark:bg-white/5">
        <LiveDot />
        <span className="font-mono text-[10px] uppercase tracking-wide text-ink-strong">live</span>
      </span>
    );
  }
  return <Chip mono>{kind === "prebuilt" ? "Prebuilt" : "Custom"}</Chip>;
}

function TemplateCard({
  t,
  pending,
  onOpen,
}: {
  t: TemplateVM;
  pending: boolean;
  onOpen: () => void;
}) {
  return (
    <Card
      interactive={!pending}
      onClick={pending ? undefined : onOpen}
      className={cn("group relative overflow-hidden", pending && "pointer-events-none opacity-70")}
    >
      <TemplateThumb thumb={t.colors} />
      {pending && (
        <div className="absolute inset-0 grid place-items-center bg-canvas/40 backdrop-blur-[1px]">
          <Loader2 className="h-5 w-5 animate-spin text-ink-strong" strokeWidth={1.75} />
        </div>
      )}
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[14px] font-medium leading-snug text-ink-strong">{t.name}</p>
          <ArrowUpRight
            className="mt-0.5 h-4 w-4 shrink-0 text-subtle transition-colors group-hover:text-ink"
            strokeWidth={1.5}
          />
        </div>
        <p className="mt-0.5 line-clamp-1 text-[12px] text-muted">{t.blurb}</p>
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <KindChip kind={t.kind} />
          <span className="font-mono text-[11px] text-subtle">
            {t.kind === "published" && t.views != null ? `${fmt(t.views)} views` : t.category}
          </span>
        </div>
      </div>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className={cn("min-w-0")}>
      <p className="font-mono text-[10px] uppercase tracking-wide text-subtle">{label}</p>
      <p className="text-[13px] text-ink-strong">{value}</p>
    </div>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ------------------------------ Page sheet -------------------------------- */
/**
 * Preview + settings for one real page. The preview renders the page's current
 * version tree READ-ONLY (no onSelect) inside a device frame, scoped to the
 * project's active design theme — this is the primary way to see a page since
 * dev has no public render. The settings half assigns a custom domain and shows
 * that domain's DNS records + verification status.
 */
function PageSheet({
  page,
  projectId,
  focusDomain,
  onClose,
  onEdit,
}: {
  page: Page;
  projectId: string | undefined;
  focusDomain: boolean;
  onClose: () => void;
  onEdit: () => void;
}) {
  const previewQ = usePage(page.id);
  const activeThemeQ = useActiveTheme(projectId);
  const domainsQ = useDomains(projectId);
  const updateMut = useUpdatePage(projectId);

  const tree = previewQ.data?.current_version?.tree ?? null;
  const tokens = activeThemeQ.data?.tokens ?? null;
  const liveable = isCustomDomain(page.domain);

  const [domainOpen, setDomainOpen] = useState(focusDomain);
  const scopeRef = useRef<HTMLDivElement>(null);
  const [scope, setScope] = useState<HTMLDivElement | null>(null);
  useEffect(() => setScope(scopeRef.current), [tree]);

  const domainItems = domainsQ.data?.items ?? [];
  const assigned = domainItems.find((d) => d.hostname === page.domain);

  return (
    <Sheet open onClose={onClose} title={page.title}>
      <div className="space-y-4">
        {/* read-only device preview */}
        <div className="rounded-card border border-hairline bg-paper-2 p-3 dark:bg-white/5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="eyebrow">Preview</span>
            <PageStatusChip status={page.status} />
          </div>
          {previewQ.isLoading ? (
            <div className="grid place-items-center rounded-card border border-dashed border-hairline bg-canvas px-6 py-14 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-subtle" strokeWidth={1.5} />
              <p className="mt-3 text-[13px] text-muted">Loading the current version…</p>
            </div>
          ) : tree ? (
            <div className="py-1">
              <DeviceFrame>
                <div className="h-full overflow-y-auto pt-5">
                  <div ref={scopeRef} className="bg-canvas">
                    <ThemeStyleInjector tokens={tokens} scope={scope} />
                    <TreeRenderer node={tree.root} />
                  </div>
                </div>
              </DeviceFrame>
            </div>
          ) : (
            <div className="grid place-items-center rounded-card border border-dashed border-hairline bg-canvas px-6 py-14 text-center">
              <Sparkles className="h-6 w-6 text-subtle" strokeWidth={1.25} />
              <p className="mt-3 text-[13px] font-medium text-ink-strong">No version to preview</p>
              <p className="mt-1 max-w-[32ch] text-[12px] leading-snug text-muted">
                Open it in the editor and ask Nara to draft something.
              </p>
            </div>
          )}
        </div>

        {/* meta */}
        <div className="grid grid-cols-2 gap-3 rounded-card bg-paper-2 p-4 dark:bg-white/5">
          <Detail label="Slug" value={`/${page.slug}`} />
          <Detail label="Status" value={STATUS_LABEL[page.status]} />
          <Detail label="Locale" value={page.locale} />
          <Detail label="Domain" value={liveable ? page.domain! : "No custom domain"} />
        </div>

        {/* primary actions */}
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Button
            variant="ghost"
            size="md"
            disabled={!liveable}
            title={liveable ? "Open the live page" : "Assign a custom domain to publish live"}
            onClick={() =>
              liveable && window.open(viewLiveUrl(page), "_blank", "noopener,noreferrer")
            }
          >
            <ExternalLink className="h-4 w-4" strokeWidth={1.75} />
            View live
          </Button>
          <Button variant="coral" size="md" onClick={onEdit}>
            <Pencil className="h-4 w-4" strokeWidth={1.75} />
            Edit in maker
          </Button>
        </div>

        {/* domain & DNS */}
        <div className="rounded-card border border-hairline">
          <button
            onClick={() => setDomainOpen((o) => !o)}
            aria-expanded={domainOpen}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
          >
            <span className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted" strokeWidth={1.5} />
              <span className="text-[14px] font-medium text-ink-strong">Domain &amp; DNS</span>
            </span>
            <ChevronDown
              className={cn("h-4 w-4 text-subtle transition-transform", domainOpen && "rotate-180")}
              strokeWidth={1.75}
            />
          </button>

          {domainOpen && (
            <div className="animate-fade-up border-t border-hairline px-4 py-4">
              <p className="mb-2 text-[12px] leading-snug text-muted">
                Serve this page on one of your project&apos;s domains.
              </p>

              {!projectId ? (
                <p className="text-[12px] text-subtle">Connect a workspace to manage domains.</p>
              ) : domainsQ.isLoading ? (
                <div className="flex items-center gap-2 text-[12px] text-subtle">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} /> Loading domains…
                </div>
              ) : domainItems.length === 0 ? (
                <p className="text-[12px] text-subtle">
                  No project domains yet. Add one in Settings → Custom domains, then assign it here.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {domainItems.map((d) => {
                    const selected = d.hostname === page.domain;
                    const tone = normalizeDomainStatus(d.status);
                    return (
                      <button
                        key={d.id}
                        disabled={updateMut.isPending}
                        onClick={() =>
                          !selected &&
                          updateMut.mutate({ pageId: page.id, body: { domain: d.hostname } })
                        }
                        className={cn(
                          "flex w-full items-center justify-between gap-2 rounded-btn border px-3 py-2 text-left transition-colors",
                          selected
                            ? "border-accent/40 bg-accent-soft"
                            : "border-hairline hover:bg-paper-2 dark:hover:bg-white/5",
                        )}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <Globe className="h-3.5 w-3.5 shrink-0 text-subtle" strokeWidth={1.5} />
                          <span className="truncate font-mono text-[12px] text-ink-strong">
                            {d.hostname}
                          </span>
                          <span
                            className={cn(
                              "shrink-0 font-mono text-[10px] uppercase tracking-wide",
                              tone === "live"
                                ? "text-emerald-600"
                                : tone === "error"
                                  ? "text-accent"
                                  : "text-amber-600",
                            )}
                          >
                            {tone === "live" ? "verified" : tone === "error" ? "error" : "pending"}
                          </span>
                        </span>
                        {selected && (
                          <Check className="h-4 w-4 shrink-0 text-accent" strokeWidth={2} />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {updateMut.isError && (
                <p className="mt-2 text-[12px] text-accent">
                  {errMessage(updateMut.error, "Couldn't assign that domain. Try again.")}
                </p>
              )}

              {/* DNS records for the currently-assigned domain */}
              {assigned && projectId && (
                <div className="mt-4">
                  <p className="eyebrow mb-2">DNS for {assigned.hostname}</p>
                  <DnsRecords domain={assigned} projectId={projectId} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Sheet>
  );
}

/* --------------------------- DNS records (compact) ------------------------ */
/** Verification status + copyable DNS records for one domain. Reuses the
 *  DomainsSection record/verify pattern, scoped to the page sheet. */
function DnsRecords({ domain, projectId }: { domain: DomainItem; projectId: string }) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);
  const status = normalizeDomainStatus(domain.status);
  const order = ["a", "cname", "txt"];
  const records = Object.entries(domain.records ?? {})
    .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    .map(([, rec]) => rec);
  if (domain.verification_token && !records.some((r) => r.type.toUpperCase() === "TXT")) {
    records.push({
      type: "TXT",
      host: "_landing-verify",
      value: `landing-verify=${domain.verification_token}`,
    });
  }

  const verifyMut = useMutation({
    mutationFn: () => builder.verifyDomain(projectId, domain.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["domains", projectId] }),
  });

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-chip px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide",
            status === "live"
              ? "bg-emerald-500/15 text-emerald-600"
              : "bg-paper-2 text-muted dark:bg-white/5",
          )}
        >
          {status === "live" ? (
            <ShieldCheck className="h-3 w-3" strokeWidth={2} />
          ) : (
            <Lock className="h-3 w-3" strokeWidth={2} />
          )}
          {status === "live" ? "verified" : status === "error" ? "needs attention" : "pending DNS"}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => verifyMut.mutate()}
          disabled={verifyMut.isPending}
        >
          {verifyMut.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
          )}
          {verifyMut.isPending ? "Checking…" : status === "live" ? "Re-check" : "Verify DNS"}
        </Button>
      </div>

      {records.length > 0 && (
        <div className="overflow-hidden rounded-btn border border-hairline">
          <div className="grid grid-cols-[56px_72px_1fr_28px] items-center gap-2 border-b border-hairline bg-paper-2 px-3 py-2 dark:bg-white/5">
            <span className="eyebrow">Type</span>
            <span className="eyebrow">Name</span>
            <span className="eyebrow">Value</span>
            <span />
          </div>
          {records.map((r, i) => (
            <div
              key={i}
              className="grid grid-cols-[56px_72px_1fr_28px] items-center gap-2 border-b border-hairline/60 px-3 py-2.5 last:border-0"
            >
              <span className="font-mono text-[12px] text-ink-strong">{r.type}</span>
              <span className="truncate font-mono text-[12px] text-muted">{r.host}</span>
              <span className="truncate font-mono text-[12px] text-muted">{r.value}</span>
              <button
                onClick={() => {
                  void navigator.clipboard?.writeText(r.value);
                  setCopied(r.value);
                  window.setTimeout(() => setCopied((c) => (c === r.value ? null : c)), 1400);
                }}
                aria-label="Copy value"
                className="grid h-7 w-7 place-items-center rounded-full text-subtle hover:bg-paper-2 hover:text-ink dark:hover:bg-white/5"
              >
                {copied === r.value ? (
                  <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} />
                ) : (
                  <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- New page sheet ----------------------------- */
/** "Add a web page": title (→ auto slug, editable) + a domain choice (verified
 *  project domains, the property domain, or a sensible default). On success it
 *  offers to open the fresh page in the editor. */
function NewPageSheet({
  projectId,
  propertyDomain,
  onClose,
  onOpenInEditor,
}: {
  projectId: string | undefined;
  propertyDomain: string | undefined;
  onClose: () => void;
  onOpenInEditor: (page: Page) => void;
}) {
  const domainsQ = useDomains(projectId);
  const createMut = useCreatePage(projectId);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [created, setCreated] = useState<Page | null>(null);

  const verified = (domainsQ.data?.items ?? []).filter(
    (d) => normalizeDomainStatus(d.status) === "live",
  );
  const fallback = propertyDomain ?? "aigen.solutions";
  const domainOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { hostname: string; verified: boolean }[] = [];
    for (const d of domainsQ.data?.items ?? []) {
      if (!seen.has(d.hostname)) {
        seen.add(d.hostname);
        out.push({ hostname: d.hostname, verified: normalizeDomainStatus(d.status) === "live" });
      }
    }
    if (!seen.has(fallback)) out.push({ hostname: fallback, verified: false });
    return out;
  }, [domainsQ.data, fallback]);

  const [domain, setDomain] = useState<string>("");
  useEffect(() => {
    if (!domain) setDomain(verified[0]?.hostname ?? fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainsQ.data]);

  const canSubmit = !!title.trim() && !!projectId && !!domain && !createMut.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const finalSlug = (slug.trim() || slugify(title)) || undefined;
    createMut.mutate(
      { title: title.trim(), slug: finalSlug, domain },
      { onSuccess: (page) => setCreated(page) },
    );
  };

  return (
    <Sheet open onClose={onClose} title="New web page">
      {created ? (
        <div className="space-y-4 text-center">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-500/15 text-emerald-600">
            <Check className="h-6 w-6" strokeWidth={2} />
          </span>
          <div>
            <p className="text-[15px] font-semibold text-ink-strong">“{created.title}” created</p>
            <p className="mt-1 font-mono text-[12px] text-muted">/{created.slug}</p>
          </div>
          <div className="flex items-center justify-center gap-3 pt-1">
            <Button variant="ghost" size="md" onClick={onClose}>
              Done
            </Button>
            <Button variant="coral" size="md" onClick={() => onOpenInEditor(created)}>
              <Pencil className="h-4 w-4" strokeWidth={1.75} />
              Open in editor
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="eyebrow mb-1.5 block">Page title</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!slugEdited) setSlug(slugify(e.target.value));
              }}
              placeholder="Spring launch"
              className="h-11 w-full rounded-btn border border-hairline bg-paper px-3 text-[14px] text-ink-strong placeholder:text-subtle focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent dark:bg-white/[0.03]"
            />
          </div>

          <div>
            <label className="eyebrow mb-1.5 block">URL slug</label>
            <div className="flex items-center gap-2 rounded-btn border border-hairline bg-paper px-3 focus-within:border-accent dark:bg-white/[0.03]">
              <span className="font-mono text-[13px] text-subtle">/</span>
              <input
                value={slug}
                onChange={(e) => {
                  setSlug(slugify(e.target.value));
                  setSlugEdited(true);
                }}
                placeholder="spring-launch"
                className="h-11 w-full bg-transparent font-mono text-[13px] text-ink-strong outline-none placeholder:text-subtle"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow mb-1.5 block">Domain</label>
            {domainsQ.isLoading && projectId ? (
              <div className="flex items-center gap-2 text-[12px] text-subtle">
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} /> Loading domains…
              </div>
            ) : (
              <div className="space-y-1.5">
                {domainOptions.map((opt) => {
                  const selected = opt.hostname === domain;
                  return (
                    <button
                      type="button"
                      key={opt.hostname}
                      onClick={() => setDomain(opt.hostname)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-btn border px-3 py-2 text-left transition-colors",
                        selected
                          ? "border-accent/40 bg-accent-soft"
                          : "border-hairline hover:bg-paper-2 dark:hover:bg-white/5",
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <Globe className="h-3.5 w-3.5 shrink-0 text-subtle" strokeWidth={1.5} />
                        <span className="truncate font-mono text-[12px] text-ink-strong">
                          {opt.hostname}
                        </span>
                        {opt.verified && (
                          <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-emerald-600">
                            verified
                          </span>
                        )}
                      </span>
                      {selected && <Check className="h-4 w-4 shrink-0 text-accent" strokeWidth={2} />}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="mt-1.5 text-[12px] text-subtle">
              Verified custom domains serve the page publicly; the default is fine to start.
            </p>
          </div>

          {createMut.isError && (
            <p className="text-[12px] text-accent">
              {errMessage(createMut.error, "Couldn't create the page. Check the details and retry.")}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <Button type="button" variant="quiet" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="coral" size="md" disabled={!canSubmit}>
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
              ) : (
                <Plus className="h-4 w-4" strokeWidth={1.75} />
              )}
              Create page
            </Button>
          </div>
        </form>
      )}
    </Sheet>
  );
}
